import React from "react";
import { Box, Typography, Link, Stack, Button } from "@mui/material";
import { useTranslation } from "react-i18next";

interface ProductMainInfoProps {
  title: string;
  categories: string[];
  specialties: string[];
  description: string;
}

const ProductMainInfo: React.FC<ProductMainInfoProps> = ({
  title,
  categories,
  specialties,
  description,
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 60%" }, pl: { md: 2 }, pr: { md: 3 } }}>
      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontWeight: 500,
          mb: 1,
          fontSize: "1.75rem",
          color: "var(--text-primary)",
        }}
      >
        {title}
      </Typography>

      <Link
        href="#"
        underline="always"
        sx={{
          color: "var(--primary-color)",
          fontSize: "0.875rem",
          display: "inline-block",
          mb: 3,
          "&:hover": { color: "var(--link-hover)" },
        }}
      >
        {t("products.manufacturers.generalElectricHealthcare")}
      </Link>

      <Box sx={{ mb: 2 }}>
        <Typography
          variant="body2"
          sx={{ color: "var(--text-secondary)", fontSize: "0.813rem" }}
        >
          {t("products.reference")}: {t("products.productReference.geB8502014")}
        </Typography>
      </Box>

      {/* Categories */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            mb: 1,
            color: "var(--text-primary)",
            fontSize: "0.875rem",
          }}
        >
          {t("products.categories")}:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {categories.map((category, index) => (
            <Button
              key={index}
              variant="outlined"
              size="small"
              sx={{
                borderColor: "var(--border-primary)",
                color: "var(--text-primary)",
                textTransform: "none",
                fontSize: "0.75rem",
                py: 0.3,
                px: 1.2,
                mb: 0.5,
                borderRadius: 0.5,
                minHeight: 28,
                "&:hover": {
                  borderColor: "var(--border-active)",
                  bgcolor: "var(--bg-hover)",
                },
              }}
            >
              {category}
            </Button>
          ))}
        </Stack>
      </Box>

      {/* Medical Specialties */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            mb: 1,
            color: "var(--text-primary)",
            fontSize: "0.875rem",
          }}
        >
          {t("products.medicalSpecialties")}:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {specialties.map((specialty, index) => (
            <Button
              key={index}
              variant="outlined"
              size="small"
              sx={{
                borderColor: "var(--border-primary)",
                color: "var(--text-primary)",
                textTransform: "none",
                fontSize: "0.75rem",
                py: 0.3,
                px: 1.2,
                mb: 0.5,
                borderRadius: 0.5,
                minHeight: 28,
                "&:hover": {
                  borderColor: "var(--border-active)",
                  bgcolor: "var(--bg-hover)",
                },
              }}
            >
              {specialty}
            </Button>
          ))}
        </Stack>
      </Box>

      {/* Description */}
      {description && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{ lineHeight: 1.6, color: "var(--text-secondary)", whiteSpace: "pre-line" }}
          >
            {description}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ProductMainInfo;